import React, { useState, useEffect, useMemo } from 'react';
import { MessageBox, Table, InputNumber, Row, Col } from 'kenshin';

// 递归计算树中每个节点的数量（父节点数量 = 子节点数量之和）
const calculateTreeCount = (nodes) => {
  let total = 0;
  nodes.forEach((node) => {
    if (node.childList && node.childList.length > 0) {
      // 递归计算子节点
      const childrenCount = calculateTreeCount(node.childList);
      node.servCatgCount = childrenCount;
    }
    total += Number(node.servCatgCount) || 0;
  });
  return total;
};

// 递归查找并更新节点
const updateTreeData = (nodes, id, value) => {
  return nodes.map((node) => {
    if (node.proServCatgId === id) {
      return { ...node, servCatgCount: value };
    }
    if (node.childList && node.childList.length > 0) {
      const newChildList = updateTreeData(node.childList, id, value);
      return {
        ...node,
        childList: newChildList,
        children: newChildList, // 同步更新 children，确保 Table 组件能正确渲染树形结构
      };
    }
    return node;
  });
};

// 获取所有节点的修改数据
const getAllNodeChanges = (nodes, result = []) => {
  nodes.forEach((node) => {
    // 添加当前节点数据
    result.push({
      id: node.proServCatgId,
      value: node.servCatgCount,
    });

    // 如果有子节点，递归处理
    if (node.childList && node.childList.length > 0) {
      getAllNodeChanges(node.childList, result);
    }
  });
  return result;
};

const ModifyCountModal = ({ visible, onCancel, onOk, data }) => {
  const [dataSource, setDataSource] = useState([]);
  const [initialTotal, setInitialTotal] = useState(0);

  useEffect(() => {
    if (visible && data?.childList) {
      // 深拷贝数据，避免直接修改原数据
      const clonedData = JSON.parse(JSON.stringify(data.childList));

      // 递归处理，确保每一层都有 children 属性指向 childList
      const addChildrenRef = (list) => {
        list.forEach((item) => {
          if (item.childList && item.childList.length > 0) {
            item.children = item.childList;
            addChildrenRef(item.childList);
          }
        });
      };
      addChildrenRef(clonedData);

      setDataSource(clonedData);
      setInitialTotal(data.servCatgCount || 0);
    }
  }, [visible, data]);

  // 实时计算当前总数
  const currentTotal = useMemo(() => {
    // 这里因为 dataSource 已经是第一层子节点列表，直接累加它们的 servCatgCount 即可
    // 但因为内部可能发生了变化，我们需要确保 dataSource 中的父节点数量已经是最新的
    // calculateTreeCount 会直接修改传入的 nodes 中的 count 值，所以我们在 render 或 handleChange 时处理比较好
    // 为避免副作用，我们在 handleChange 中处理完数据后再 setDataSource
    return dataSource.reduce(
      (sum, item) => sum + (Number(item.servCatgCount) || 0),
      0,
    );
  }, [dataSource]);

  const handleChange = (value, record) => {
    // 1. 更新对应节点的值
    let newData = updateTreeData(dataSource, record.proServCatgId, value);

    // 2. 重新计算整棵树的父节点数量
    // 由于 updateTreeData 返回的是新数组，我们可以安全地对其进行 mutable 操作来重新计算 count
    // 或者我们可以写一个 immutable 的计算函数，这里为了简单直接遍历计算
    // calculateTreeCount 函数会修改 nodes 中的 servCatgCount
    // 所以我们需要先深拷贝或者确保 calculateTreeCount 的副作用是预期的
    // updateTreeData 已经产生了一个新的对象结构（对于变化的路径），但对于未变化的节点引用可能还是旧的
    // 为了安全起见，我们对 newData 做一次深拷贝再计算，或者优化 calculateTreeCount
    // 实际上 calculateTreeCount 只是为了计算总数并更新中间节点的 count，
    // 我们可以在 calculateTreeCount 中返回总数，并同时更新节点属性

    const clonedNewData = JSON.parse(JSON.stringify(newData));
    calculateTreeCount(clonedNewData);
    setDataSource(clonedNewData);
  };

  const handleOk = () => {
    const allChanges = getAllNodeChanges(dataSource);
    onOk(currentTotal, allChanges);
  };

  const columns = [
    {
      title: '名称',
      dataIndex: 'servCatgName',
      key: 'servCatgName',
      render(name, row) {
        return `${row.servCatgCode}${name}`;
      },
    },
    {
      title: '数量',
      dataIndex: 'servCatgCount',
      key: 'servCatgCount',
      width: 150,
      render: (text, record) => {
        const isLeaf = !record.childList || record.childList.length === 0;
        return (
          <InputNumber
            value={text}
            onChange={(val) => handleChange(val, record)}
            disabled={!isLeaf}
            min={0}
            stepType="inside"
            style={{ width: '100%' }}
            className={!isLeaf ? 'disabled-input' : ''}
          />
        );
      },
    },
  ];

  return (
    <MessageBox
      title="数量修改"
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      width={800}
      destroyOnClose
      zIndex={1001}
      maskClosable={false}
      bodyStyle={{ padding: '24px' }}
    >
      <Row gutter={24} style={{ marginBottom: 20, alignItems: 'center' }}>
        <Col>
          <span style={{ fontWeight: 'bold', fontSize: 16 }}>当前数量</span>
        </Col>
        <Col>
          <div
            style={{
              border: '1px solid #d9d9d9',
              padding: '4px 11px',
              width: 100,
              textAlign: 'center',
              background: '#f5f5f5',
            }}
          >
            {initialTotal}
          </div>
        </Col>
        <Col>
          <span style={{ fontWeight: 'bold', fontSize: 16 }}>修改为</span>
        </Col>
        <Col>
          <div
            style={{
              border: '1px solid #d9d9d9',
              padding: '4px 11px',
              width: 100,
              textAlign: 'center',
            }}
          >
            {currentTotal}
          </div>
        </Col>
      </Row>

      <Table
        dataSource={dataSource}
        columns={columns}
        rowKey="proServCatgId"
        pagination={false}
        defaultExpandAllRows={true}
        childrenColumnName="childList"
        scroll={{ y: 400 }}
      />

      <div
        style={{
          marginTop: 20,
          textAlign: 'center',
          fontSize: 18,
          fontWeight: 'bold',
        }}
      >
        总数 <span style={{ marginLeft: 20 }}>{currentTotal}</span>
      </div>
    </MessageBox>
  );
};

export default ModifyCountModal;
